import type { RemunerationMonth } from './remuneration.js';

type ExpenseMonth = Pick<
  RemunerationMonth,
  'period' | 'expenses' | 'expensesComplete' | 'clientReceiptsCents'
>;

export interface AnnualExpenseSummary {
  readonly year: number;
  readonly fromPeriod: string;
  readonly throughPeriod: string | null;
  readonly expectedMonths: number;
  readonly knownMonths: number;
  readonly totalPaidCents: bigint | null;
  readonly averagePaidCents: bigint | null;
  readonly excludedDeboursCents: bigint | null;
  readonly receiptsCents: bigint | null;
  readonly ratioBasisPoints: number | null;
}

/**
 * Sorties professionnelles payées, pas des charges comptables ou une marge.
 * Un mois entamé ne baisse pas artificiellement la moyenne des mois clos.
 * Un mois absent ou dupliqué n'est jamais un zéro. Les débours explicitement
 * qualifiés restent dans le détail mensuel, mais sont exclus de cette mesure
 * comme ils le sont des recettes clients auxquelles elle est rapportée.
 */
export class ExpenseHistory {
  public constructor(private readonly months: readonly ExpenseMonth[]) {}

  public annual(year: number, asOfMonth: string): AnnualExpenseSummary {
    if (!Number.isInteger(year) || year < 2020 || !/^20\d{2}-(0[1-9]|1[0-2])$/u.test(asOfMonth))
      throw new Error('Période de dépenses invalide.');
    const periods = Array.from(
      { length: year === 2020 ? 4 : 12 },
      (_, index) => `${year}-${String(index + (year === 2020 ? 9 : 1)).padStart(2, '0')}`,
    ).filter((period) => period < asOfMonth);
    const known = periods.flatMap((period) => {
      const candidates = this.months.filter((month) => month.period === period);
      const row = candidates.length === 1 ? candidates[0] : undefined;
      return row?.expensesComplete === true && row.expenses !== undefined ? [row] : [];
    });
    let total = 0n;
    let debours = 0n;
    for (const month of known)
      for (const expense of month.expenses!) {
        const name = expense.name
          .trim()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/gu, '')
          .toLowerCase();
        if (/^(?:frais de )?debours$/u.test(name)) debours += expense.paidCents;
        else total += expense.paidCents;
      }
    // Exactement les mêmes mois et la même unité : ne pas opposer une somme
    // de paiements TTC à un CA HT, ni des années de couverture différente.
    const receipts =
      known.length && known.every((month) => month.clientReceiptsCents != null)
        ? known.reduce((sum, month) => sum + month.clientReceiptsCents!, 0n)
        : null;
    return {
      year,
      fromPeriod: periods[0] ?? `${year}-01`,
      throughPeriod: periods.at(-1) ?? null,
      expectedMonths: periods.length,
      knownMonths: known.length,
      totalPaidCents: known.length ? total : null,
      averagePaidCents: known.length ? roundDivision(total, BigInt(known.length)) : null,
      excludedDeboursCents: known.length ? debours : null,
      receiptsCents: receipts,
      ratioBasisPoints: receipts === null ? null : expenseShareBasisPoints(total, receipts),
    };
  }
}

/** Un ratio ne se calcule pas sur des encaissements nuls ou négatifs. */
export function expenseShareBasisPoints(paid: bigint, receipts: bigint): number | null {
  if (receipts <= 0n) return null;
  const result = Number(roundDivision(paid * 10000n, receipts));
  return Number.isSafeInteger(result) ? result : null;
}

/** Arrondi au centime le plus proche, symétrique pour un remboursement net. */
function roundDivision(value: bigint, divisor: bigint): bigint {
  const absolute = value < 0n ? -value : value;
  const result = (absolute + divisor / 2n) / divisor;
  return value < 0n ? -result : result;
}
